import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CreateNewColorPack, SaveColor } from '../../../../../../connection/colors';
import { UploadFile } from '../../../../../../connection/uploads';
import { addNewColorPack, saveColorPack, setDesignColors } from '../../../../../../store/actions/admin/create-event';
import { EventsState } from '../../../../../../store/types';
import { ColorOptions } from '../../../../../../types/working-model';
import LargeButton from '../../../../../general-ui/button/large-button';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import ImageEditor from '../../../../../general-ui/image-editor/image-editor';
import ModalComponent from '../../../../../general-ui/modal/modal';
import WaitingIndicator from '../../../../../general-ui/waiting-indicator/waiting-indicator';
import ColorEditor from '../../../settings/design/colors/color-editor';
import { parseColor } from '../../../../document-head';

interface IThemePreviewProps {
	color?: [string, number]; //color hex, opacity
	imgSrc?: string;
	isCurrent?: boolean;
	onClick: any;
}

const ThemePreview: React.FC<IThemePreviewProps> = (props) => {
	const {
		color,
		imgSrc,
		isCurrent,
		onClick: handleClick
	} = props;

	return (
		<button
			style={{
				backgroundColor: color ? parseColor(color) : "",
				backgroundImage: imgSrc ? `url(${imgSrc})` : "",
				width: '40px',
				height: '40px',
				borderRadius: '50%'
			}}
			className={`round color-option ${isCurrent ? 'selected' : ''}`}
			onClick={handleClick}
		></button>);
};

export enum EContentColorOverrideNames {
	title = 'Title',
	description = 'Description',
	custom_heading = 'Custom Heading',
	modules = 'Modules',
	background = 'Background',
	navItems = 'Navigation'
}

export enum EModuleStyleOverrides {
	title = 'title',
	description = 'description',
	custom_heading = 'custom_heading',
	primary_button = 'primary_button',
	secondary_butto = 'secondary_button',
	dropdowns = 'dropdowns'
}


interface IEditSectionColorsModalProps {
	open: boolean;
	title: EContentColorOverrideNames;
	backgroundImage?: string;
	onFinish: (colorKey: string) => void;
	onClose: () => void;
	allowBackgroundImage?: boolean;
}

const EditSectionColorsModal: React.FC<IEditSectionColorsModalProps> = (props) => {
	const {
		open,
		title,
		backgroundImage,
		onFinish: handleFinish,
		onClose: handleClose,
		allowBackgroundImage = true,
	} = props;

	const {
		CreateEventReducer: {
			workingEvent
		},
		AuthReducer: { token, user },
	} = useSelector((state: EventsState) => state);

	const dispatch = useDispatch();

	const [uploading, setUploading] = useState(false);
	const [colors, setColors] = useState<ColorOptions | undefined>(workingEvent?.settings.design.colors.colors);
	const [displayCustomColor, setDisplayCustomColor] = useState(false);
	const [displayAddImage, setDisplayAddImage] = useState(false);
	const [imageFile, setImageFile] = useState<string | undefined>(backgroundImage);
	const [resizedImage, setResizedImage] = useState<File | null>();
	const [colorChange, setColorChange] = useState('');
	const [customColor, setCustomColor] = useState<[string, number] | null>(null);

	const imageRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		if (workingEvent?.settings?.design?.colors?.colors) {
			setColors(workingEvent?.settings?.design?.colors?.colors);
		}
	}, [workingEvent?.settings?.design?.colors?.colors]);

	useEffect(() => {
		setImageFile(backgroundImage);
	}, [backgroundImage]);

	const customColors = useMemo(() => {
		return Object.entries(colors ?? {}).filter((color: any) => color[0].includes('customColor'));
	}, [colors]);

	async function addCustomColor() {
		const allCustomColorByNumber = customColors.map((color: any) => Number(color[0].replace(/\D/g, '')));

		const lastCustomColorNumber = allCustomColorByNumber.reduce((a: number, c: number) => c > a ? c : a, 0);

		const workingColor = { ...workingEvent!.settings.design.colors };
		workingColor.colors = { ...colors!, [`customColor${lastCustomColorNumber + 1}`]: customColor! };

		if (!Number(workingEvent?.settings.design.colors.channel)) {
			const newColor = await CreateNewColorPack(token!, workingColor);
			dispatch(addNewColorPack(newColor));
			dispatch(setDesignColors(newColor));
		} else {
			await SaveColor(token!, workingColor);
			workingEvent!.settings.design.colors.colors = {
				...workingEvent!.settings.design.colors.colors,
				[`customColor${lastCustomColorNumber + 1}`]: customColor!
			};
			dispatch(saveColorPack(workingColor));
		}

	}

	const palletteColors = useMemo(() => {
		return Object.entries(colors ?? {}).filter((color: any) => !color[0].includes('customColor'));
	}, [colors]);

	const backgroundImgElement = useMemo(() => {
		return imageFile ?
			<ThemePreview
				key={imageFile}
				imgSrc={imageFile}
				onClick={() => setColorChange(imageFile as string)}
				isCurrent={colorChange === imageFile}
			/> : <></>;
	}, [imageFile, setColorChange, colorChange]);


	const contentStylingOptions = palletteColors.map((color: [string, [string, number]]) => {
		if (color[0] && color[1]) {
			return <ThemePreview
				key={color[1][0] + color[0]}
				color={color[1]}
				onClick={() => setColorChange(color[0] as string)}
				isCurrent={colorChange === color[0]}
			/>;
		}
	});

	const customColorElements = customColors.map((color: [string, [string, number]]) => {
		if (color[0] && color[1]) {
			return <ThemePreview
				key={color[1][0]}
				color={color[1]}
				onClick={() => setColorChange(color[0] as string)}
				isCurrent={colorChange === color[0]}
			/>;

		}
	});

	async function handleAddImage(f: File | FileList) {
		if (f instanceof File) {
			setUploading(true);
			setResizedImage(f);
			const uploadedImage = await UploadFile(user!, token!, f);
			setImageFile(uploadedImage);
			setUploading(false);
		}
	}

	function handleRestoreDefault() {
		setColorChange("");
	}

	const workingStyleOptionMap = {
		background: (
			<div className="style-options">
				{
					displayAddImage ?
						imageFile ?
							<ImageEditor
								originalImage={imageFile}
								onFinished={setResizedImage}
							/>
							:
							<div className="upload-background-image">
								<LargeButton
									title={"Upload Image"}
									subtitle={"Use PNG or JPG files up to 10 Mb size"}
									allowedFileTypes={['image/png', 'image/jpeg', 'image/gif']}
									onFile={handleAddImage}
									style={{ marginTop: 40, marginBottom: 40, paddingTop: 36, paddingBottom: 36 }}
								/>
							</div>
						:
						<>
							<p>Background</p>
							<div className="options">
								{contentStylingOptions}
								{customColorElements}
								<button
									className="small-button round"
									onClick={() => setDisplayCustomColor(!displayCustomColor)}
								>
									<Icon name={ICONS.ADD} size={16} color={COLORS.WHITE} />
								</button>
								{
									// for modules that shouldnt have background image
									allowBackgroundImage && (
										<>
											<p>or</p>
											{backgroundImgElement}
											<button
												className="small-button round"
												onClick={() => {
													setDisplayCustomColor(false);
													setDisplayAddImage(true);
												}}
											>
												<Icon name={ICONS.IMAGE} size={20} color={COLORS.WHITE} />
											</button>
										</>
									)
								}
							</div>
						</>
				}
			</div>),
		content: (
			<div className="style-options">
				<p>{title}</p>
				<div className="options">
					{contentStylingOptions}
					{customColorElements}
					<button onClick={() => setDisplayCustomColor(true)} className="small-button round">
						<Icon name={ICONS.ADD} size={16} color={COLORS.WHITE} />
					</button>
				</div>
			</div>
		),
	};

	return (
		<ModalComponent
			cancellable={true}
			closeable={false}
			open={!!open}
			onRequestClose={handleClose}
			title={displayAddImage ? "Upload background photo" : "Customize Section Colors"}
			size={displayAddImage ? undefined : "large"}
			footer={(
				<>
					{
						displayAddImage && imageFile ? (
							<>
								<button
									className="no-style replace-logo"
									onClick={() => {
										if (!uploading) { imageRef.current?.click(); }
									}}
								>
									<Icon
										name={ICONS.UPLOAD}
										color={COLORS.CYAN}
										size={12}
									/>
									Replace Photo
								</button>
								<input
									type="file"
									accept="image/jpeg,image/png,image/gif"
									onChange={(e) => handleAddImage((e.target.files?.[0] as File))}
									style={{ display: 'none' }}
									ref={imageRef}
								/>
							</>
						) : (
							<button
								className="no-style restore-default text-only-primary"
								onClick={handleRestoreDefault}
							>
								Restore default
							</button>
						)
					}
					<button
						style={{ marginRight: 16 }}
						onClick={() => {
							if (displayAddImage) {
								setDisplayAddImage(false);
								return;
							}
							handleClose();
						}}
					>
						Cancel
					</button>
					<button
						disabled={uploading}
						className="primary"
						onClick={async () => {
							if (displayAddImage && resizedImage) {
								await handleAddImage(resizedImage);
								setDisplayAddImage(false);
							} else {
								const classOverride = title === EContentColorOverrideNames.background
									? colorChange
									: colorChange + "-color";
								//Checks to see if the string going back is a backgound URL
								// If so just, send that back, if not send back the ful class override name
								handleFinish(colorChange.includes(".") ? colorChange : classOverride);
								handleClose();
							}
						}
						}
					>
						{
							uploading ?
								<WaitingIndicator />
								:
								'Done'
						}
					</button>
				</>
			)}
		>
			<div className="style-modal-content">
				{title === EContentColorOverrideNames.background ? workingStyleOptionMap.background : workingStyleOptionMap.content}
				{
					displayCustomColor &&
					<div className="custom-color-editor">
						<ColorEditor
							color={customColor ?? ['', 1]}
							title={"Custom Color"}
							onChange={(s: string, n: number) => setCustomColor([s, n])}
						/>
						<button
							className="small-button round"
							onClick={() => {
								addCustomColor();
								setDisplayCustomColor(false);
							}}><Icon name={ICONS.ADD} size={16} color={COLORS.WHITE} /></button>
					</div>

				}
			</div>

		</ModalComponent>
	);
};

export default EditSectionColorsModal;
